"use client"

import Link from "next/link";
import * as React from "react";
import { useState } from "react";
import { createForm } from "@/components/form/create";
import { FormInput } from "@/components/form/input";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ActionSubmit } from "@/lib/action";

import { QRCodeSVG } from "qrcode.react";

import { AnimatedLogo } from "@/components/brand/logo.animated";
import { CircleAlert } from "lucide-react";

import { AuthFormProps } from "@/types/types";
import { login, askResetPassword } from "@/lib/auth";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils/utils";
import { showCenteredToast } from "@/lib/action/feedback";



export const AuthHeader = () => (
  <div className="grid gap-2 text-center">
    <h1 className="text-3xl font-bold">Accedi</h1>
    <p className="text-balance text-muted-foreground">
      Inserisci la tua email per accedere al tuo account
    </p>
  </div>
);



export const AuthForm = ({type}: AuthFormProps) => {

  const [resultMessage, setResultMessage] = useState<string>("");
  const [prevStateRef, setPrevStateRef] = useState<any>(undefined);
  const [passwordError, setPasswordError] = useState<string>("");
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false); // Stato per gestire la visualizzazione del form

  // Inizializza email dallo storage o stringa vuota
  const [email, setEmail] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_email') || "";
    }
    return "";
  });

  // Wrapper per adattare handleSignIn alla firma richiesta da createForm
  const handleSignInWrapper = async (data: { 
    email: string; 
    password: string; 
    newpassword?: string; 
    confirmpassword?: string;
    rstnewpassword?: string;
    confirmrstnewpassword?: string;
    confirmationcode?: string;
    totp?: string 
  }) => {
    // Validazione password quando si sta impostando una nuova password
    if (data.newpassword && data.confirmpassword) {
      if (data.newpassword !== data.confirmpassword) {
        setPasswordError("Le password non corrispondono");
        return;
      }
      setPasswordError("");
    }

    const formData = new FormData();
    
    // Se abbiamo una nuova email, aggiorniamola sia nello state che nel localStorage
    if (data.email) {
      localStorage.setItem('auth_email', data.email);
      setEmail(data.email);
    }
    
    // Usa l'email dallo state se non c'è nei dati del form
    const currentEmail = data.email || email;
    
    formData.append("email", currentEmail);
    if (data.password) formData.append("password", data.password);
    if (data.newpassword) formData.append("newpassword", data.newpassword);
    if (data.rstnewpassword) formData.append("rstnewpassword", data.rstnewpassword);
    if (data.confirmationcode) formData.append("confirmationcode", data.confirmationcode);
    if (data.totp) formData.append("totp", data.totp);

    const result = await login({
      email: currentEmail,
      password: data.password,
      newpassword: data.newpassword,
      rstnewpassword: data.rstnewpassword,
      confirmationcode: data.confirmationcode,
      totp: data.totp
    });

    if (result && result.message) {
      setResultMessage(result.message);
    }

    // Se result.status = NEXT_STEP_REQUIRED serve un altro step
    if (result.status === "NEXT_STEP_REQUIRED") {
      setPrevStateRef(result);
    }


    if (result.status === "RESET_PASSWORD_DONE"){
      showCenteredToast(result.message || "Reset password done", "success");
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);
    }

    if (result.status === "TOTP_VERIFIED" || result.status === "LOGGED_IN") {
      localStorage.setItem("loginSuccess", result.message || "Benvenuto!");
      window.location.href = "/";
    } else {
      setPrevStateRef(result);
    }

    // Se c'è un errore nell'inserimento del Codice oAuth
    if (result.status === "ERROR") {

      showCenteredToast(result.message || "Errore in fase di login", "error");

      // Ritardiamo il redirect per permettere di vedere il toast
      setTimeout(() => {
        window.location.href = "/auth";
      }, 3000);
    }
  };

  const LoginForm = createForm({
    //action: login,
    action: handleSignInWrapper
    //action: type==="oauth"? handleSignInWrapper : login,
    //id: "login-form"
  });

  const cancelLogin = () => {
    setShowForgotPassword(false);

    window.location.href = "/auth";
  }

  
  const requestResetCode = async () => {
    try {
      if (!email) {
        showCenteredToast("Inserisci un'email valida prima di richiedere un nuovo codice", "error");
        return;
      }
      const result = await askResetPassword(email);
      if (result.status === "NEXT_STEP_REQUIRED" || result.status === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        showCenteredToast("Nuovo codice di conferma inviato alla tua email", "success");
      } else {
        showCenteredToast(result.message || "Errore durante la richiesta del nuovo codice", "error");
        //cancelLogin();
      }
    } catch (error: any) {
      console.log(error.message);
      showCenteredToast("Errore durante la richiesta del nuovo codice", "error");
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true); // Mostra il form per la password dimenticata
  };

  return (
    <LoginForm.Form className="space-y-4 mt-8">
      {!showForgotPassword && !prevStateRef && (
        <>
          <div className="grid gap-2 text-left mb-8 w-full">
            <h1 className="text-3xl font-bold">Accedi</h1>
            <p className="text-balance text-muted-foreground">
              Inserisci la tua email per accedere al tuo account
            </p>
          </div>
          <LoginForm.Field name="email">
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <FormInput 
                  type="email" 
                  placeholder="m@esempio.com" 
                  disabled={Boolean(prevStateRef)}
                  defaultValue={email}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </LoginForm.Field>
          <LoginForm.Field name="password" >
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <FormInput type="password" placeholder="********" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </LoginForm.Field>
          
          <div style={{ marginTop: "4rem" }} className="flex justify-between align-middle gap-4 mr-8 mt-24 ">
            <ActionSubmit type="submit" className="flex-1">
              Accedi
            </ActionSubmit>
            <button
              type="button"
              className="text-xs text-muted-foreground underline"
              onClick={handleForgotPassword} // Mostra il form per la password dimenticata
            >
              Password dimenticata?
            </button>
          </div>
        </>
      )}

      <div className="mt-4">
        { (prevStateRef && (prevStateRef.nextStep?.nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" )) &&
          (
            <div className="space-y-8">
              <div className="grid gap-2 text-left">
                <h1 className="text-3xl font-bold">Accedi</h1>
                <p className="text-balance text-muted-foreground">
                  Inserisci una nuova password prima di continuare
                </p>
              </div>
              <LoginForm.Field name="email">
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="email" 
                      placeholder="m@esempio.com" 
                      disabled={Boolean(prevStateRef)}
                      defaultValue={email}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>
              
              <LoginForm.Field name="newpassword">
                <FormItem>
                  <FormLabel>Nuova password</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="password" 
                      placeholder="Inserisci la nuova password" 
                      required 
                      minLength={8}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>

              <LoginForm.Field name="confirmpassword">
                <FormItem>
                  <FormLabel>Conferma nuova password</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="password" 
                      placeholder="Conferma la nuova password" 
                      required 
                      minLength={8}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>

              {passwordError && (
                <div className="text-red-500 text-sm mt-2">
                  {passwordError}
                </div>
              )}
              
              <div className="flex gap-4 mt-8">
                <ActionSubmit type="submit" className="flex-1 bg-primary">
                  Continua
                </ActionSubmit>
                <Button
                  type="button"
                  variant={"ghost"}
                  className={cn(
                    `relative overflow-hidden transition-all bg-primary-foreground duration-300 ease-in-out`,
                  )}
                  onClick={cancelLogin}
                >
                  Annulla
                </Button>
              </div>
            </div>
          )
        }
        { (showForgotPassword || (prevStateRef && (prevStateRef.nextStep?.nextStep === "RESET_PASSWORD"))) &&
          (
            <div className="space-y-8">
              <div className="grid gap-2 text-left">
                <h1 className="text-3xl font-bold">Reset Password</h1>
                <p className="text-balance text-muted-foreground">
                  Inserisci una nuova password prima di continuare
                </p>
              </div>
              <LoginForm.Field name="email">
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="email" 
                      placeholder="m@esempio.com" 
                      disabled={Boolean(prevStateRef)}
                      defaultValue={email}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>
              
              <LoginForm.Field name="rstnewpassword">
                <FormItem>
                  <FormLabel>Nuova password</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="password" 
                      placeholder="Inserisci la nuova password" 
                      required 
                      minLength={8}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>

              <LoginForm.Field name="confirmrstnewpassword">
                <FormItem>
                  <FormLabel>Conferma nuova password</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="password" 
                      placeholder="Conferma la nuova password" 
                      required 
                      minLength={8}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>

              <LoginForm.Field name="confirmationcode">
                <FormItem>
                  <FormLabel>Codice di conferma (inviato sulla tua email)</FormLabel>
                  <FormControl>
                    <FormInput 
                      type="text" 
                      placeholder="Inserisci la nuova codice di conferma" 
                      required 
                      minLength={6}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </LoginForm.Field>
              
              <div style={{ marginTop: "4rem" }} className="flex gap-4 mt-8">
                <ActionSubmit type="submit" className="flex-1 bg-primary">
                  Continua
                </ActionSubmit>
                <Button
                  type="button"
                  variant={"ghost"}
                  className={cn(
                    `relative overflow-hidden transition-all bg-primary-foreground duration-300 ease-in-out`,
                  )}
                  onClick={cancelLogin}
                >
                  Annulla
                </Button>
              </div>

              <div className="flex justify-left mt-12">
                <button
                  type="button"
                  className="mt-4 cursor-pointer text-xs text-blue-500"
                  onClick={requestResetCode}
                >
                  Non hai ricevuto il codice? <b>Clicca per richiederne uno.</b>
                </button>
              </div>

              {passwordError && (
                <div className="text-red-500 text-sm mt-2">
                  {passwordError}
                </div>
              )}
            </div>
          )
        }
        { (prevStateRef && (prevStateRef.status === "NEXT_STEP_REQUIRED" )) &&
          (
            <>
              {(prevStateRef.nextStep.nextStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" || prevStateRef.nextStep.nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") && (
               <>           
                  { (prevStateRef?.nextStep.secret) ? (
                    <>
                      <div className="grid gap-2 text-left mb-8">
                        <h1 className="text-3xl font-bold">Configura l&apos;App di Autenticazione</h1>
                        <p className="text-balance text-muted-foreground">
                          Scansiona il QR Code con la tua app di autenticazione
                        </p>
                      </div>
                      <QRCodeSVG value={prevStateRef.nextStep.href}/>
                      <p className="mt-8">Oppure inserisci manualmente questo codice:</p>
                      <code className="block bg-secondary p-2 rounded">{prevStateRef?.nextStep.secret}</code>
                    </>
                  ) : (
                    <>
                      <div className="grid gap-2 text-left mb-8">
                        <h1 className="text-3xl font-bold">Accedi</h1>
                        <p className="text-balance text-muted-foreground">
                          Inserisci il codice OTP indicato nella tua Authenticator App
                        </p>
                      </div>
                    </>
                  )}
              
                  <div className="mt-8">
                    <LoginForm.Field name="totp" >
                      <FormItem>
                        <FormLabel>Codice OTP</FormLabel>
                        <FormControl>
                          <FormInput type="password" placeholder="Inserisci il codice OTP" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </LoginForm.Field>
                    
                    <div className="flex gap-4 mt-8">
                      <ActionSubmit type="submit" className="flex-1">
                        Verifica
                      </ActionSubmit>
                      <Button
                        type="button"
                        variant={"ghost"}
                        className={cn(
                          `relative overflow-hidden transition-all bg-primary-foreground duration-300 ease-in-out`,
                        )}
                        onClick={cancelLogin}
                      >
                        Annulla
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) 
        }
        </div>
        {prevStateRef && prevStateRef.status === "ERROR" ? (
          <div className="flex flex-col items-center justify-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-4">
            <CircleAlert className="h-6 w-6 text-red-500 mb-2" />
            <p className="text-sm font-semibold">{resultMessage}</p>
            <p className="text-xs mt-1">Ritorno alla pagina di login...</p>
          </div>
        ) : (!prevStateRef && !showForgotPassword) && (
          <div className="flex justify-center mt-24">
            <Link href="/auth/signup" className="mt-4 cursor-pointer text-xs text-blue-500">
              Non hai un account? Contatta l&apos;amministratore,
              oppure invia una email a <b>info@mindmash.it</b>
            </Link>
          </div>
        )}

    </LoginForm.Form>
  );
};

export const AuthFooter = () => (
  <div className="mt-4 text-center text-sm">
    Non hai un account?{" "}
    <Link href="#" className="underline">
      Registrati
    </Link>
  </div>
);
export const AuthImage = () => (
  <div className="hidden h-full lg:flex flex-col items-center justify-center gap-6">
    <AnimatedLogo className="w-48" />
    <h1 className="text-6xl font-extrabold text-primary tracking-wide font-sans bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
      ELYSIUM
    </h1>
    {/*     <div className="relative w-48">
      <Image src={logoDark} alt="logo" />
    </div> */}
  </div>
);

export function AuthContent({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="mx-auto grid w-[350px] gap-6">{children}</div>
    </div>
  );
}

export function Auth({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full min-h-screen  lg:grid lg:grid-cols-2 ">
      {children}
    </div>
  );
}
